import React from 'react';
import { Box } from '@mui/material';
import { PROGRAM_TYPE_COLORS } from './constants';
import { PROGRAM_TYPE_LABELS } from './ProgramCard';
import type { ProgramTypeKey } from './types';
import { MOBILE_SX } from '../../../../../../mobile/mobileApp';

// Program-type filter order. An empty selection means every type is shown, so
// the framework defaults to all programs and the chips only ever narrow it.
const TYPE_FILTER_ORDER: ProgramTypeKey[] = ['role-excellence', 'development', 'standards'];

/**
 * Role Excellence / Development / Standards filter chips, above the framework.
 * Each carries its own type colour as a dot; a selected chip fills with a faint
 * tint of that colour. Multi-select, so two types can show at once. Shared by
 * the admin and learner Learning Lifecycle dashboards.
 */
const ProgramTypeFilter: React.FC<{
  selected: ProgramTypeKey[];
  onToggle: (key: ProgramTypeKey) => void;
}> = ({ selected, onToggle }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexShrink: 0,
      // Phone: one row that scrolls sideways rather than wrapping a chip's label.
      [MOBILE_SX]: {
        gap: '6px',
        flexShrink: 1,
        minWidth: 0,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' }
      }
    }}
  >
    {TYPE_FILTER_ORDER.map((key) => {
      const on = selected.includes(key);
      const color = PROGRAM_TYPE_COLORS[key];
      return (
        <Box
          key={key}
          component="button"
          type="button"
          onClick={() => onToggle(key)}
          aria-pressed={on}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '7px',
            fontFamily: 'Inter, sans-serif',
            fontSize: 12.5,
            fontWeight: 500,
            lineHeight: 1,
            cursor: 'pointer',
            borderRadius: '999px',
            px: '12px',
            py: '6px',
            transition: 'color 0.15s, border-color 0.15s, background-color 0.15s',
            color: on ? color : '#B6B6B6',
            border: `1px solid ${on ? color : '#333333'}`,
            bgcolor: on ? `${color}1F` : '#161616',
            '&:hover': { color: on ? color : '#FDFDFD', borderColor: on ? color : '#565656' },
            [MOBILE_SX]: { flexShrink: 0, whiteSpace: 'nowrap', minHeight: 36 }
          }}
        >
          <Box sx={{ width: 8, height: 8, borderRadius: '9999px', bgcolor: color, flex: '0 0 auto' }} />
          {PROGRAM_TYPE_LABELS[key]}
        </Box>
      );
    })}
  </Box>
);

export default ProgramTypeFilter;
